import {
  patchDecisionOverride,
  setDecisionOverride
} from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/individualLoanDetailsService';
import { AuthenticationContextType, useAuthenticationContext } from 'Foundation/Authentication';
import {
  FlyoutStatusLoanData,
  Loan,
  MasterPoolRemarkPayload,
  MasterPoolRemarkResponse,
  PoolProcessingStatusCode,
  PortfolioInsuranceType,
  ValidationRemarkPayload,
  ValidationRemarkResponse
} from '../types';
import {
  FlyoutType,
  generateMasterpoolLoan,
  generateMasterPoolPayload,
  generateValidationPayload,
  generateValidationRemarkLoan
} from './utils';
import { getLoansStatus } from '../service/forceStatusService';
import { LoanInventoryTableFields } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/types';
import { useTranslation } from 'react-i18next';
import { getUrlParam } from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolDetails/utils/masterPoolDetailsUtils';

interface OnAddLoansInterface {
  loans: string[];
  approvalStatus: string | null;
  flyoutLoans: Loan[];
  typeOfPortfolioInsurance?: PortfolioInsuranceType;
  pptCode: string | null;
  applicationID: string | null;
  typeOfFlyout?: FlyoutType;
  loansData?: LoanInventoryTableFields[];
  isFromLoansInventory?: boolean;
  masterPoolCmhcAccountNumber?: string;
  requestUrl?: string;
  authenticationContext: AuthenticationContextType;
}
export const onAddLoans = async ({
  loans,
  approvalStatus,
  flyoutLoans,
  authenticationContext,
  typeOfPortfolioInsurance,
  pptCode,
  applicationID,
  typeOfFlyout,
  loansData,
  isFromLoansInventory,
  masterPoolCmhcAccountNumber,
  requestUrl
}: OnAddLoansInterface): Promise<{
  success: boolean;
  loans: any[];
  error: string[];
  errorCode?: string[];
}> => {
  const duplicateLoans = 'HBT_ERR_3003';
  const portfolioId = getUrlParam('pfID');
  let listOfPortfolioId: number[] = [];
  if (Boolean(portfolioId)) {
    listOfPortfolioId = portfolioId.split(',').map(Number);
  }
  try {
    let data: any;
    if (typeOfPortfolioInsurance === 'validationReview') {
      const flyoutLoansData = flyoutLoans.map((loan) => loan.validationReviewLoanData!);

      const convertLoanNumberToFlyoutStatusLoanData: FlyoutStatusLoanData[] = loans.map((loan) => {
        const completeLoan = loansData?.find(
          (data) => String(data.cmhcAccountNumber) === loan || data.lenderReferenceNumber === loan
        );

        if (completeLoan) {
          return {
            accountNumber: loan,
            sequenceNumber: completeLoan.loanSequenceNumber ?? null
          };
        }

        return {
          accountNumber: loan,
          sequenceNumber: null
        };
      });

      let payload: ValidationRemarkPayload = generateValidationPayload(
        [...flyoutLoansData, ...convertLoanNumberToFlyoutStatusLoanData],
        approvalStatus,
        true,
        pptCode,
        applicationID,
        listOfPortfolioId,
        loansData
      );
      if (typeOfFlyout === 'updateRemark') {
        data = await patchDecisionOverride({
          authenticationContext,
          request: payload
        });

        if (data.statusCode && data.statusCode !== 200) {
          return {
            success: false,
            loans: [],
            error: [data.error.errorMessage],
            errorCode: [data.error.errorCode]
          };
        }
        const newLoans = generateValidationRemarkLoan(data as ValidationRemarkResponse[]);
        return { success: true, loans: newLoans, error: [] };
      } else {
        data = await setDecisionOverride({
          authenticationContext,
          request: payload
        });

        if (data.status && data.statusCode !== 200) {
          return {
            success: false,
            loans: [],
            error: [data.error.errorMessage],
            errorCode: [data.error.errorCode]
          };
        }

        const newLoans = generateValidationRemarkLoan(data as ValidationRemarkResponse[]);
        return { success: true, loans: newLoans, error: [] };
      }
    } else {
      const getFlyOutLoansAccontNumbers = flyoutLoans.map(
        (loan: Loan) => loan.accountNumber as string
      );

      let payload: MasterPoolRemarkPayload = generateMasterPoolPayload(
        [...getFlyOutLoansAccontNumbers, ...loans],
        approvalStatus,
        true
      );
      if (
        isFromLoansInventory &&
        (typeOfFlyout === 'changeStatus' || typeOfFlyout === 'updateRemark')
      ) {
        payload.isFromLoansInventory = true;
        payload.masterPoolCmhcAccountNumber = masterPoolCmhcAccountNumber;
      }
      data = await getLoansStatus({
        authenticationContext,
        payload,
        requestQuery: requestUrl
      });
    }

    data = generateMasterpoolLoan(data as MasterPoolRemarkResponse[]);

    return { success: true, loans: data, error: [] };
  } catch (error) {
    if (error.error && error.error.errorCode === duplicateLoans) {
      return { success: false, loans: [], error: [duplicateLoans], errorCode: [duplicateLoans] };
    }
    return { success: false, loans: [], error: ['Error getting loan status'] };
  }
};
